import { gql } from '@apollo/client';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LOGIN_PATH } from 'src/common/constants';
import { MutationSet, SecondaryStore } from 'src/common/interfaces';
import { RecordState } from 'src/common/string-enum';
import { InfoMessage, InfoWord } from 'src/enum/messsage-enum';
import { insertMstNetRate, updateMstNetRate } from 'src/graphql/mutations';
import { getMstNetRate, getMstPrimarySales, getMstProduct, getMstSecondarySales } from 'src/graphql/queries';
import { useAppLazyQuery, useAppMutation } from 'src/libs/hooks';
import utility from '../../libs/utility';
import MainLayout from '../main-layout';

interface MstNetRate {
  mnr_primary_cd: string;
  mps_primary_nm: string;
  mnr_secondary_cd: string;
  mss_secondary_nm: string;
  mnr_product_cd: string;
  mpr_product_nm: string;
  mnr_software_net_rate: number | null;
  mnr_template_net_rate: number | null;
  mnr_license_net_rate: number | null;
  mnr_useware_net_rate: number | null;
  mnr_maintenance_net_rate: number | null;
  recordState?: RecordState;
  isBeginData: boolean;
}

interface MstPrimarySales {
  mps_primary_cd: string;
  mps_primary_nm: string;
}

interface MstSecondarySales {
  mss_primary_cd: string;
  mss_secondary_cd: string;
  mss_secondary_nm: string;
}

interface MstProduct {
  mpr_product_cd: string;
  mpr_product_nm: string;
}

const NetRateManage = () => {
  const header = [
    '一次店',
    '二次店',
    '製品区分',
    'ソフトウェア仕切率',
    'テンプレート仕切率',
    'ライセンス仕切率',
    'ユースウェア仕切率',
    '保守仕切率',
  ];
  const [mstNetRate, setMstNetRate] = useState<Array<MstNetRate>>([]);
  const [mstNetRateDisp, setMstNetRateDisp] = useState<Array<MstNetRate>>([]);
  const [primarySalesOption, setPrimarySalesOption] = useState<Array<MstPrimarySales>>([]);

  // 二次店の選択肢は、一次店の選択状況に応じるため、DBの値と表示用の値の2種類を保持する
  const [secondarySalesOptionRaw, setSecondarySalesOptionRaw] = useState<Array<SecondaryStore>>([]);
  const [secondarySalesOption, setSecondarySalesOption] = useState<Array<SecondaryStore>>([]);
  const [productOption, setProductOption] = useState<Array<MstProduct>>([]);
  const [selectPrimarySales, setSelectPrimarySales] = useState('');
  const [selectSecondarySales, setSelectSecondarySales] = useState('');
  const [selectProduct, setSelectProduct] = useState('');

  const { confirmAlert, showAlertMessage, registerData } = utility;
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuthenticator((context) => [context.user]);

  const [updateFunc] = useAppMutation({ query: gql(updateMstNetRate) });
  const [insertFunc] = useAppMutation({ query: gql(insertMstNetRate) });
  const [searchMstNetRate, { loading: isNetRateLoading }] = useAppLazyQuery({ query: gql(getMstNetRate) });
  const [searchMstPrimarySales, { loading: isPStoreLoading }] = useAppLazyQuery({ query: gql(getMstPrimarySales) });
  const [searchMstSecondarySales, { loading: isSStoreLoading }] = useAppLazyQuery({
    query: gql(getMstSecondarySales),
  });
  const [searchMstProduct, { loading: isProductLoading }] = useAppLazyQuery({ query: gql(getMstProduct) });

  const betweenNumber = (val: number, min: number, max: number) => {
    return val >= min && val <= max;
  };

  const renderTableData = useMemo(() => {
    if (!mstNetRateDisp) return;
    return mstNetRateDisp.map((netRate: MstNetRate, index: number) => {
      return (
        <tr className="hover:opacity-80 odd:bg-[#e2efd9]" key={index}>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className="bg-transparent pl-[5px]"
              type="text"
              name="name"
              maxLength={100}
              value={netRate?.mps_primary_nm ?? ''}
              disabled={true}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className="bg-transparent pl-[5px]"
              type="text"
              name="name"
              maxLength={100}
              value={netRate?.mss_secondary_nm ?? ''}
              disabled={true}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className="bg-transparent pl-[5px]"
              type="text"
              name="name"
              maxLength={100}
              value={netRate?.mpr_product_nm ?? ''}
              disabled={true}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className="border-transparent pl-[5px] bg-transparent"
              onChange={(event) => {
                if (!betweenNumber(Number(event.target.value), 0, 100)) return;
                handleChangeNetRate(index, {
                  ...{ mnr_software_net_rate: Number(event.target.value) },
                  ...{ recordState: netRate.isBeginData ? RecordState.UPDATE : RecordState.INSERT },
                });
              }}
              type="number"
              name="softwareNetRate"
              min={0}
              max={100}
              step="0.01"
              value={netRate?.mnr_software_net_rate ?? ''}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className="border-transparent pl-[5px] bg-transparent"
              onChange={(event) => {
                if (!betweenNumber(Number(event.target.value), 0, 100)) return;
                handleChangeNetRate(index, {
                  ...{ mnr_template_net_rate: Number(event.target.value) },
                  ...{ recordState: netRate.isBeginData ? RecordState.UPDATE : RecordState.INSERT },
                });
              }}
              type="number"
              name="templateNetRate"
              min={0}
              max={100}
              step="0.01"
              value={netRate?.mnr_template_net_rate ?? ''}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className="border-transparent pl-[5px] bg-transparent"
              onChange={(event) => {
                if (!betweenNumber(Number(event.target.value), 0, 100)) return;
                handleChangeNetRate(index, {
                  ...{ mnr_license_net_rate: Number(event.target.value) },
                  ...{ recordState: netRate.isBeginData ? RecordState.UPDATE : RecordState.INSERT },
                });
              }}
              type="number"
              name="licenseNetRate"
              min={0}
              max={100}
              step="0.01"
              value={netRate?.mnr_license_net_rate ?? ''}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className="border-transparent pl-[5px] bg-transparent"
              onChange={(event) => {
                if (!betweenNumber(Number(event.target.value), 0, 100)) return;
                handleChangeNetRate(index, {
                  ...{ mnr_useware_net_rate: Number(event.target.value) },
                  ...{ recordState: netRate.isBeginData ? RecordState.UPDATE : RecordState.INSERT },
                });
              }}
              type="number"
              name="usewareNetRate"
              min={0}
              max={100}
              step="0.01"
              value={netRate?.mnr_useware_net_rate ?? ''}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className="border-transparent pl-[5px] bg-transparent"
              onChange={(event) => {
                if (!betweenNumber(Number(event.target.value), 0, 100)) return;
                handleChangeNetRate(index, {
                  ...{ mnr_maintenance_net_rate: Number(event.target.value) },
                  ...{ recordState: netRate.isBeginData ? RecordState.UPDATE : RecordState.INSERT },
                });
              }}
              type="number"
              name="maintenanceNetRate"
              min={0}
              max={100}
              step="0.01"
              value={netRate?.mnr_maintenance_net_rate ?? ''}
            />
          </td>
        </tr>
      );
    });
  }, [mstNetRateDisp]);

  const setMstNetRateWithSort = (data: MstNetRate[], setState: (value: React.SetStateAction<any[]>) => void) => {
    data.sort(
      (a: MstNetRate, b: MstNetRate) =>
        Number(a.mnr_primary_cd) - Number(b.mnr_primary_cd) ||
        Number(a.mnr_secondary_cd) - Number(b.mnr_secondary_cd) ||
        Number(a.mnr_product_cd) - Number(b.mnr_product_cd)
    );

    setState(data);
  };

  const handleChangeNetRate = (index: number, valueObj: Partial<MstNetRate>) => {
    const updatedNetRate = [...mstNetRate];
    updatedNetRate[index] = { ...updatedNetRate[index], ...valueObj };

    setMstNetRate(updatedNetRate);
    setMstNetRateDisp(updatedNetRate);
  };

  const renderTableHeader = () => {
    return header.map((key, index) => (
      <th
        className="p-[8px] border-[1px] border-solid border-[#fff] pt-[12px] pb-[12px] text-center bg-[#a8d08d] text-[#fff]"
        key={index}
      >
        {key.toUpperCase()}
      </th>
    ));
  };

  const onPressConfirmDisplay = () => {
    Auth.currentAuthenticatedUser()
      .then(() => onDisplayData())
      .catch(() => {
        signOut();
        navigate(LOGIN_PATH, { replace: true, state: { from: location } });
      });
  };

  const onPressConfirmRegister = () => {
    Auth.currentAuthenticatedUser()
      .then(() =>
        confirmAlert(InfoMessage.CONFIRM_REGISTR, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, onRegisterData)
      )
      .catch(() => {
        signOut();
        navigate(LOGIN_PATH, { replace: true, state: { from: location } });
      });
  };

  const initHeaderData = ({
    mstPrimarySales,
    mstSecondarySales,
    mstProduct,
  }: {
    mstPrimarySales: MstPrimarySales[];
    mstSecondarySales: SecondaryStore[];
    mstProduct: MstProduct[];
  }) => {
    setPrimarySalesOption(
      mstPrimarySales.sort(
        (a: MstPrimarySales, b: MstPrimarySales) => Number(a.mps_primary_cd) - Number(b.mps_primary_cd)
      )
    );
    setSecondarySalesOptionRaw(
      mstSecondarySales.sort(
        (a: SecondaryStore, b: SecondaryStore) => Number(a.mss_secondary_cd) - Number(b.mss_secondary_cd)
      )
    );
    setProductOption(
      mstProduct.sort((a: MstProduct, b: MstProduct) => Number(a.mpr_product_cd) - Number(b.mpr_product_cd))
    );
  };

  const loadInitData = async () => {
    const initDataList = await Promise.all([
      searchMstPrimarySales({ variables: { input: {} } }),
      searchMstSecondarySales({ variables: { input: {} } }),
      searchMstProduct({ variables: { input: {} } }),
      searchMstNetRate({ variables: { input: {} } }),
    ]);
    const primarySales = initDataList[0].data.getMstPrimarySales;
    const secondarySales = initDataList[1].data.getMstSecondarySales;
    const product = initDataList[2].data.getMstProduct;
    const netRate = initDataList[3].data.getMstNetRate;

    initHeaderData({
      mstPrimarySales: primarySales,
      mstSecondarySales: secondarySales,
      mstProduct: product,
    });

    initTableData({
      mstPrimarySales: primarySales,
      mstSecondarySales: secondarySales,
      mstProduct: product,
      mstNetRate: netRate,
    });
  };

  useEffect(() => {
    loadInitData();
  }, []);

  const renderPullDown = useMemo(() => {
    return (
      <div>
        <label>
          <b>
            <label className="p-[8px] pt-[14.5px] pb-[14.5px] text-center bg-[#6699ff90] text-[#fff]">一次店</label>
          </b>
          <select
            className="className=border-[#000000] border-[1px] p-[10px] mt-[25px] mb-[25px] text-center h-[50px] w-[150px] mr-5"
            value={selectPrimarySales}
            onChange={(e) => {
              setSelectPrimarySales(e.target.value);

              // 一次店を選択しなおすので、二次店の選択状態と選択しを初期化する
              setSelectSecondarySales('');

              setSecondarySalesOption(
                secondarySalesOptionRaw.filter((item) => item.mss_primary_cd.toString() === e.target.value)
              );
            }}
          >
            <option value={undefined} key={undefined}>
              {''}
            </option>
            {primarySalesOption.map((primarySales: MstPrimarySales) => (
              <option value={primarySales.mps_primary_cd} key={primarySales.mps_primary_cd}>
                {primarySales.mps_primary_nm}
              </option>
            ))}
          </select>
        </label>
        <label
          onClick={() => {
            if (selectPrimarySales === '') {
              showAlertMessage('', InfoMessage.SELECT_PRIMARY_STORE, InfoWord.ERROR, InfoWord.OK);
            }
          }}
        >
          <b>
            <label className="p-[8px] pt-[14.5px] pb-[14.5px] text-center bg-[#6699ff90] text-[#fff]">二次店</label>
          </b>
          <select
            className="className=border-[#000000] border-[1px] p-[10px] mt-[25px] mb-[25px] text-center h-[50px] w-[150px]  mr-5"
            value={selectSecondarySales}
            onChange={(e) => {
              setSelectSecondarySales(e.target.value);
            }}
            onClick={() => {
              if (selectPrimarySales === '') {
                showAlertMessage('', InfoMessage.SELECT_PRIMARY_STORE, InfoWord.ERROR, InfoWord.OK);
              }
            }}
            disabled={selectPrimarySales === '' ? true : false}
          >
            <option value={undefined} key={undefined}>
              {''}
            </option>
            {secondarySalesOption.map((secondarySales: MstSecondarySales) => (
              <option value={secondarySales.mss_secondary_cd} key={secondarySales.mss_secondary_cd}>
                {secondarySales.mss_secondary_nm}
              </option>
            ))}
          </select>
        </label>
        <label>
          <b>
            <label className="p-[8px] pt-[14.5px] pb-[14.5px] text-center bg-[#6699ff90] text-[#fff]">製品区分</label>
          </b>
          <select
            className="className=border-[#000000] border-[1px] p-[10px] mt-[25px] mb-[25px] text-center h-[50px] w-[150px] mr-5"
            value={selectProduct}
            onChange={(e) => {
              setSelectProduct(e.target.value);
            }}
          >
            <option value={undefined} key={undefined}>
              {''}
            </option>
            {productOption.map((product: MstProduct) => (
              <option value={product.mpr_product_cd} key={product.mpr_product_cd}>
                {product.mpr_product_nm}
              </option>
            ))}
          </select>
        </label>
        <button
          onClick={onPressConfirmDisplay}
          className="border-[#000000] border-[1px] p-[10px] mt-[25px] mb-[25px] text-center h-[50px] w-[100px] "
        >
          <b>表示</b>
        </button>
      </div>
    );
  }, [
    primarySalesOption,
    secondarySalesOption,
    productOption,
    selectPrimarySales,
    selectSecondarySales,
    selectProduct,
  ]);

  const prepareDataApplyApi = (netRateData: MstNetRate) => {
    return {
      mnr_primary_cd: netRateData.mnr_primary_cd,
      mnr_secondary_cd: netRateData.mnr_secondary_cd,
      mnr_product_cd: netRateData.mnr_product_cd,
      mnr_software_net_rate: netRateData.mnr_software_net_rate,
      mnr_template_net_rate: netRateData.mnr_template_net_rate,
      mnr_license_net_rate: netRateData.mnr_license_net_rate,
      mnr_useware_net_rate: netRateData.mnr_useware_net_rate,
      mnr_maintenance_net_rate: netRateData.mnr_maintenance_net_rate,
    };
  };

  const onDisplayData = async () => {
    const data: MstNetRate[] = [];

    mstNetRate.forEach((item) => {
      if (selectPrimarySales !== '' && selectPrimarySales.toString() !== item.mnr_primary_cd.toString()) return;
      if (selectSecondarySales !== '' && selectSecondarySales.toString() !== item.mnr_secondary_cd.toString()) return;
      if (selectProduct !== '' && selectProduct.toString() !== item.mnr_product_cd.toString()) return;
      data.push(item);
    });

    setMstNetRateWithSort(data, setMstNetRateDisp);
  };

  const onRegisterData = async () => {
    const mstNetRateNewList = [...mstNetRate];
    const insertData = mstNetRateNewList.filter((item: MstNetRate) => item?.recordState === RecordState.INSERT);
    const updateData = mstNetRateNewList.filter((item: MstNetRate) => item?.recordState === RecordState.UPDATE);

    const updSet: MutationSet<MstNetRate> = {
      recordState: RecordState.UPDATE,
      data: updateData,
      func: (data: MstNetRate) => updateFunc({ variables: { input: prepareDataApplyApi(data) } }),
    };

    const insSet: MutationSet<MstNetRate> = {
      recordState: RecordState.INSERT,
      data: insertData,
      func: (data: MstNetRate) => insertFunc({ variables: { input: prepareDataApplyApi(data) } }),
    };

    const searchFunc = async () => {
      setSelectPrimarySales('');
      setSelectSecondarySales('');
      setSelectProduct('');
      await loadInitData();
    };

    registerData({
      mutationSets1: [updSet, insSet],
      validates: () => true,
      searchFunc,
    });
  };

  const initTableData = ({
    mstPrimarySales,
    mstSecondarySales,
    mstProduct,
    mstNetRate,
  }: {
    mstPrimarySales: MstPrimarySales[];
    mstSecondarySales: SecondaryStore[];
    mstProduct: MstProduct[];
    mstNetRate: MstNetRate[];
  }) => {
    const initList: MstNetRate[] = [];

    mstSecondarySales.forEach((sStore) => {
      const filterPrimarySales: MstPrimarySales[] = mstPrimarySales.filter(
        (pStore) => pStore.mps_primary_cd === sStore.mss_primary_cd
      );
      mstProduct.forEach((product) => {
        const netRate: MstNetRate[] = mstNetRate.filter(
          (nRate: MstNetRate) =>
            String(nRate.mnr_primary_cd) === String(sStore.mss_primary_cd) &&
            String(nRate.mnr_secondary_cd) === String(sStore.mss_secondary_cd) &&
            String(nRate.mnr_product_cd) === String(product.mpr_product_cd)
        );

        const item: MstNetRate = {
          mnr_license_net_rate: netRate[0]?.mnr_license_net_rate ?? null,
          mnr_maintenance_net_rate: netRate[0]?.mnr_maintenance_net_rate ?? null,
          mnr_software_net_rate: netRate[0]?.mnr_software_net_rate ?? null,
          mnr_template_net_rate: netRate[0]?.mnr_template_net_rate ?? null,
          mnr_useware_net_rate: netRate[0]?.mnr_useware_net_rate ?? null,
          mnr_primary_cd: sStore.mss_primary_cd,
          mps_primary_nm: filterPrimarySales[0]?.mps_primary_nm,
          mnr_secondary_cd: sStore.mss_secondary_cd,
          mss_secondary_nm: sStore.mss_secondary_nm,
          mnr_product_cd: product.mpr_product_cd,
          mpr_product_nm: product.mpr_product_nm,
          recordState: RecordState.NONE,
          isBeginData: netRate.length > 0,
        };

        initList.push(item);
      });
    });
    setMstNetRateWithSort(initList, setMstNetRate);
    setMstNetRateWithSort(initList, setMstNetRateDisp);
  };

  return (
    <MainLayout>
      <div className="justify-center items-center">
        <div className="flex flex-col justify-center items-center">
          <p className="p-[20px]">仕切率管理</p>
          <div className="flex w-4/5">
            <button
              onClick={onPressConfirmRegister}
              className="border-[#000000] border-[1px] p-[10px] mt-[25px] mb-[25px] text-center h-[50px] w-[100px] flex justify-center items-center"
            >
              <b>登録</b>
            </button>
          </div>
          <div className="flex w-4/5">{renderPullDown}</div>
          <table className="text-center border-collapse border-[3px] border-solid border-[#ddd] w-4/5 td:p-[10px]">
            <tbody>
              <tr className="hover:opacity-80 even:bg-[#e2efd9]">{renderTableHeader()}</tr>
              {isNetRateLoading || isPStoreLoading || isSStoreLoading || isProductLoading ? (
                <tr className="hover:opacity-80 even:bg-[#e2efd9]">
                  <td
                    colSpan={header.length}
                    className="text-center animate-pulse p-[8px] border-[1px] border-solid border-[#fff]"
                  >
                    Loading...
                  </td>
                </tr>
              ) : (
                renderTableData
              )}
            </tbody>
          </table>
        </div>
      </div>
    </MainLayout>
  );
};

export default NetRateManage;
